// Angular
import { AbstractControl, FormGroup } from '@angular/forms';

/**
 * Finds a control by name, looking at the root first and then inside nested FormGroups.
 *
 * The OPF party form is flat (`ssn`, `dob`, `phone`, `ext`, `email` all sit on the root), while the
 * SubF Add Party sidecar groups the same fields by section (`individualPartyInfo.ssn`,
 * `partyContactInfo.phone`). The court-configuration requirement rules — which field is required,
 * and the rule that a hidden field must never be required — are identical for both and are worth
 * keeping in one place, so the lookup bends instead of the rules being duplicated (#172448).
 *
 * The root is checked first, so a flat form resolves exactly as it did before this existed.
 *
 * FormArrays are deliberately not searched. Their rows repeat, so "the" control of a given name is
 * ambiguous there, and every field these rules touch lives on a group.
 *
 * @param root - The form to search.
 * @param name - The control name, e.g. `'ssn'`.
 * @returns The matching control, or null when no group holds one by that name.
 */
export function findFormControl(
  root: AbstractControl | null | undefined,
  name: string,
): AbstractControl | null {
  if (!root) {
    return null;
  }

  const direct = root.get(name);
  if (direct) {
    return direct;
  }

  if (!(root instanceof FormGroup)) {
    return null;
  }

  for (const child of Object.values(root.controls)) {
    if (child instanceof FormGroup) {
      const found = findFormControl(child, name);
      if (found) {
        return found;
      }
    }
  }

  return null;
}
